import * as React from 'react';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import {
  Collapse,
  Typography,
  Link as MuiLink,
  Paper,
  Button,
} from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { IconChevronDown } from '@maiia/design-system';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import Link from '../../atoms/Link/Link';

import { useTranslation } from '@/src/i18n';

type CenterNavigationRowProps = {
  center: any;
  // eslint-disable-next-line react/no-unused-prop-types
  card?: any;
  onClick?: () => void;
};

type PractitionerCenterNavigationRowProps = {
  center: any;
  card: any;
  onClick?: () => void;
};

type CenterNavigationProps = {
  rootCenter: any;
  card: any;
};

type CentersTreeProps = {
  centers: any[];
  card: any;
};

const CENTER_NAVIGATION_TYPE_CENTER = 'CENTER';
const CENTER_NAVIGATION_TYPE_PRACTITIONER = 'PRACTITIONER';

const CenterNavigationRow = ({ center, onClick }: CenterNavigationRowProps) => {
  const { id, name, childCenters = [] } = center;
  return (
    <Link href={center.url || '/'} legacyBehavior>
      <TreeItem
        nodeId={id}
        label={<Typography variant="body1">{name}</Typography>}
        onClick={onClick}
      >
        {childCenters
          .filter(childCenter => childCenter.isVisibleOnline)
          .map(childCenter => (
            <CenterNavigationRow key={childCenter.id} center={childCenter} />
          ))}
      </TreeItem>
    </Link>
  );
};

const PractitionerCenterNavigationRow = ({
  center,
  card,
  onClick,
}: PractitionerCenterNavigationRowProps) => {
  const { id, name } = center;
  return (
    <Link
      href={`${card?.url ? `${card.url}?centerId=${center.id}` : '/'}`}
      legacyBehavior
    >
      <TreeItem
        nodeId={id}
        label={<Typography variant="body1">{name}</Typography>}
        onClick={onClick}
      />
    </Link>
  );
};

const CenterNavigation = ({ rootCenter, card }: CenterNavigationProps) => {
  const getCenterTree = center => {
    const { childCenters = [], ...rest } = center;
    const ret = [{ ...rest }];
    return childCenters.length === 0
      ? ret
      : ret.concat(
          childCenters.reduce(
            (result, childCenter) => result.concat(getCenterTree(childCenter)),
            [],
          ),
        );
  };
  const flattenCenters = getCenterTree(rootCenter);
  const rowComponent = React.useMemo(
    () =>
      React.createElement(
        card.type === CENTER_NAVIGATION_TYPE_CENTER
          ? CenterNavigationRow
          : PractitionerCenterNavigationRow,
        {
          center: rootCenter,
          card,
          onClick: () => {
            analyticsEvent({
              category: 'PractitionerOrCenterPage',
              action: 'ClickChangeCenter',
            });
          },
        },
      ),
    [card, rootCenter],
  );
  return (
    <TreeView
      expanded={flattenCenters.map(({ id }) => id)}
      selected={[card.center?.id]}
    >
      {rowComponent}
    </TreeView>
  );
};

const CentersTreeWrapper = styled(Paper)(
  ({ theme }) => css`
    background-color: ${theme.palette.background.xLightBlue};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacing(1.3)};
    box-shadow: ${theme.shadows[1]};
    :hover {
      box-shadow: ${theme.shadows[2]};
    }
  `,
);

const CollapseWrapper = styled(Collapse)(
  ({ theme }) => css`
    position: absolute;
    top: 50px;
    left: 0px;
    width: 100%;
    z-index: 1;

    .MuiPaper-root {
      padding: ${theme.spacing(1, 0)};
      background-color: ${theme.palette.background.xLightBlue};
    }

    .MuiTreeItem-label p {
      font-weight: ${theme.typography.button.fontWeight};
      padding: ${theme.spacing(0.5)};
    }

    ul li ul li .MuiTreeItem-label p {
      font-weight: normal;
    }
  `,
);

// 4 cases
// - No center (or not visible)
// - Only one center
// - Multiple centers (user can change with a dropdown)
// - Center with children (on center page)
const CentersTree = ({ centers, card }: CentersTreeProps) => {
  const { center: selectedCenter } = card;
  const [isOpen, setOpen] = React.useState(false);
  const { t } = useTranslation();
  const centersVisibles = centers.filter(center => !center.isSecret);
  const hasCenters = centers.length > 1;
  const hasOnlyCenter =
    centersVisibles.length <= 1 && !centersVisibles?.[0]?.childCenters?.length;
  const toggleChecked = React.useCallback(() => {
    if (hasOnlyCenter) return;

    analyticsEvent({
      category: 'PractitionerOrCenterPage',
      action: 'ClickCentersList',
    });
    setOpen(!isOpen);
  }, [isOpen]);

  if (!hasCenters && centersVisibles?.[0]?.childCenters?.length === 0) {
    return null;
  }

  return (
    <CentersTreeWrapper>
      {hasOnlyCenter ? (
        <Typography
          sx={theme => ({
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            span: {
              fontWeight: theme.typography.button.fontWeight,
            },
          })}
        >
          <Typography component="span">{t('consultation_place')}</Typography>
          <Typography variant="h6" component="span">
            {selectedCenter?.name}
          </Typography>
        </Typography>
      ) : (
        <Button
          onClick={toggleChecked}
          title={t('choose_consultation_place')}
          aria-label={t('choose_consultation_place')}
          data-cy="btn-center-navigation"
          sx={theme => ({
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            color: theme.palette.primary.main,
            p: 0,
            // override border to get the same result when it is not a button
            border: 0,
            '&:hover': {
              border: 0,
            },
            span: {
              fontWeight: theme.typography.button.fontWeight,
            },
          })}
        >
          <Typography color="text.primary" component="span">
            {t('consultation_place')}
          </Typography>
          <Typography
            variant="h6"
            component="span"
            sx={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            &nbsp;{selectedCenter?.name}
          </Typography>
          <Typography component="span" sx={{ ml: 1 }}>
            <IconChevronDown />
          </Typography>
        </Button>
      )}
      {card.type === CENTER_NAVIGATION_TYPE_PRACTITIONER &&
        selectedCenter?.isVisibleOnline &&
        !selectedCenter?.isSecret && (
          <Link href={selectedCenter?.url ?? '/'} passHref legacyBehavior>
            <MuiLink
              sx={theme => ({
                zIndex: 2,
                pl: theme.spacing(1.5),
                fontStyle: 'italic',
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
              })}
              data-cy="go-to-selected-center"
              onClick={() =>
                analyticsEvent({
                  category: 'PractitionerOrCenterPage',
                  action: 'ClickViewCenter',
                })
              }
            >
              {t('center_navigation_view_center')}
            </MuiLink>
          </Link>
        )}
      {!hasOnlyCenter && (
        <CollapseWrapper in={isOpen}>
          <Paper data-testid="center-tree">
            {centersVisibles.map(center => (
              <CenterNavigation
                key={center.id}
                rootCenter={center}
                card={card}
              />
            ))}
          </Paper>
        </CollapseWrapper>
      )}
    </CentersTreeWrapper>
  );
};
export default CentersTree;
